import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { api, attachAuth } from "@/lib/api";

const Ctx = createContext({ user: null, login: async () => {}, logout: () => {} });
const KEY = "kinos.auth";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [booting, setBooting] = useState(true);

  // Load token from localStorage on first mount.
  useEffect(() => {
    const raw = localStorage.getItem(KEY);
    if (!raw) { setBooting(false); return; }
    try {
      const { token: t } = JSON.parse(raw);
      if (t) {
        attachAuth(t);
        setToken(t);
        api.me()
          .then((u) => setUser(u))
          .catch(() => {
            localStorage.removeItem(KEY);
            attachAuth(null);
            setToken(null);
          })
          .finally(() => setBooting(false));
        return;
      }
    } catch (_) { /* ignore */ }
    setBooting(false);
  }, []);

  async function login(email, password) {
    const { token: t, user: u } = await api.login(email, password);
    localStorage.setItem(KEY, JSON.stringify({ token: t }));
    attachAuth(t);
    setToken(t);
    setUser(u);
    return u;
  }

  function logout() {
    api.logout().catch(() => {});
    localStorage.removeItem(KEY);
    attachAuth(null);
    setToken(null);
    setUser(null);
  }

  function applyToken(t) {
    localStorage.setItem(KEY, JSON.stringify({ token: t }));
    attachAuth(t);
    setToken(t);
  }

  return (
    <Ctx.Provider value={{
      user, token, booting, login, logout, applyToken,
      isAdmin: user?.role === "admin",
      hasModule: (mod) => hasModule(user, mod),
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}

export function hasRole(user, ...allowed) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return allowed.includes(user.role);
}

export function hasModule(user, mod) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return (user.permissions || []).includes(mod);
}
